import { LockKeyhole } from "lucide-react";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { formatExpiry } from "./options/formatExpiry";
import { useAuth } from "../contexts/AuthContext";
import {
  useDiscoverV4Detail,
  type DiscoverV4Composition,
} from "../hooks/useDiscoverV4Detail";
import type { Visibility } from "../types/subscription";
import { formatNumber, formatSignedPercentage } from "../utils/numberUtils";
import { isAddBlocked, isCompositionGlossed } from "../utils/productGloss";

export interface UnlockedHolding {
  key: string;
  type: "Stock" | "Option" | "Basket";
  name: string;
  description: string;
  weight: number;
}

export interface ProductShareUnlockedHoldingsProps {
  productType: "BASKET" | "PORTFOLIO";
  providerId: number;
  productId: number;
  providerName: string;
  visibility: Visibility;
  total: number;
  onLoaded: (details: { shownCount: number; isPartial: boolean }) => void;
  onSubscribeClick: () => void;
}

export function productDetailPath(
  productType: "BASKET" | "PORTFOLIO",
  providerId: number,
  productId: number
): string {
  return `/alpha/discover/${productType.toLowerCase()}/${providerId}/${productId}`;
}

function buildUnlockedHoldings(composition: DiscoverV4Composition): UnlockedHolding[] {
  const stocks: UnlockedHolding[] = composition.stocks.map((stock) => ({
    key: `stock-${stock.conId}`,
    type: "Stock",
    name: stock.ticker || String(stock.conId),
    description: stock.name || "Stock position",
    weight: stock.weight,
  }));
  const baskets: UnlockedHolding[] = composition.baskets.map((basket) => ({
    key: `basket-${basket.basketId}`,
    type: "Basket",
    name: basket.basketName || `Basket ${formatNumber(basket.basketId, 0)}`,
    description: `Basket - ${formatNumber(basket.stocks.length, 0)} stocks`,
    weight: basket.weight,
  }));
  const options: UnlockedHolding[] = composition.options.map((option) => ({
    key: `option-${option.conId}`,
    type: "Option",
    name: option.symbol || String(option.conId),
    description: [
      option.expiration ? formatExpiry(option.expiration.replace(/-/g, "")) : null,
      option.strike ? formatNumber(option.strike) : null,
      option.right === "P" || option.right === "PUT" ? "Put" : option.right ? "Call" : null,
    ].filter(Boolean).join(" ") || "Option position",
    weight: option.weight,
  }));
  return [...stocks, ...baskets, ...options];
}

const rowGridClassName = "grid min-h-12 grid-cols-[28px_minmax(0,1fr)_82px] items-center border-b border-v7-ink/10 px-3 py-3 last:border-b-0 sm:grid-cols-[48px_1fr_100px_96px] sm:px-4";

/**
 * The holdings table once the viewer is signed in. Reads the same investor
 * catalog call the Alpha product page uses, so what unlocks here is exactly
 * what the viewer would see on Plutus: everything for a PUBLIC product, a
 * partial set for a SUBSCRIBER product they are not subscribed to. Returned
 * weights and directions stay visible, matching the in-app detail page.
 */
const ProductShareUnlockedHoldings: React.FC<ProductShareUnlockedHoldingsProps> = ({
  productType,
  providerId,
  productId,
  providerName,
  visibility,
  total,
  onLoaded,
  onSubscribeClick,
}) => {
  const { userConfig, getUserConfigValue } = useAuth();
  const { composition, isLoadingComposition, isPartial, error, tags } = useDiscoverV4Detail({
    productType: productType === "BASKET" ? "basket" : "portfolio",
    providerId,
    productId,
  });
  const isRetirementAccount = userConfig?.data?.isretirement === "true";
  const isGlossed = isCompositionGlossed({
    visibility,
    isPartial,
    addBlocked: isAddBlocked(tags, isRetirementAccount),
    isCustomer: getUserConfigValue("iscustomer") === "true",
    isBeta: getUserConfigValue("isbeta") === "true",
  });
  const glossClassName = isGlossed ? " select-none blur-[5px] opacity-60" : "";

  const holdings = useMemo(
    () => (composition ? buildUnlockedHoldings(composition) : []),
    [composition]
  );
  const hiddenCount = Math.max(0, total - holdings.length);
  const loadedKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (isLoadingComposition || !composition) return;
    const key = `${productType}:${providerId}:${productId}:${holdings.length}:${isPartial}`;
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;
    onLoaded({ shownCount: holdings.length, isPartial });
  }, [composition, holdings.length, isLoadingComposition, isPartial, onLoaded, productId, productType, providerId]);

  if (isLoadingComposition) {
    return (
      <div data-testid="unlocked-holdings-loading" aria-busy="true">
        {Array.from({ length: Math.min(Math.max(total, 1), 8) }).map((_, index) => (
          <div key={`loading-${index}`} className={rowGridClassName}>
            <span className="font-mono text-v7-ink/40">{formatNumber(index + 1, 0)}</span>
            <span className="h-3 w-24 animate-pulse rounded bg-v7-mist sm:w-32" />
            <span className="hidden h-3 w-12 animate-pulse rounded bg-v7-mist sm:block" />
            <span className="ml-auto h-3 w-12 animate-pulse rounded bg-v7-mist sm:w-14" />
          </div>
        ))}
      </div>
    );
  }

  if (error || !composition) {
    return (
      <div className="px-4 py-10 text-center text-[13px] leading-[19px] text-v7-rust">
        We could not load the positions right now. Please try again later.
      </div>
    );
  }

  return (
    <div data-testid="unlocked-holdings" data-glossed={String(isGlossed)}>
      {holdings.map((holding, index) => {
        const isShort = holding.weight < 0;
        return (
          <div key={holding.key} className={`${rowGridClassName} text-sm`}>
            <span className="row-span-2 font-mono text-v7-ink/60 sm:row-span-1">{formatNumber(index + 1, 0)}</span>
            <span className="row-span-2 min-w-0 sm:row-span-1">
              <span className="block truncate font-mono font-medium text-v7-ink sm:inline">{holding.name}</span>
              <span className="block truncate text-xs text-v7-ink/60 sm:ml-2 sm:inline">
                {holding.description}
              </span>
            </span>
            <span className={`col-start-3 row-start-2 text-right text-[10px] font-medium sm:row-start-1 sm:text-left sm:text-xs ${isShort ? "text-v7-rust" : "text-v7-success"}${glossClassName}`}>
              {isShort ? "SHORT" : "LONG"}
            </span>
            <span className={`col-start-3 row-start-1 text-right font-mono font-medium sm:col-start-4 ${isShort ? "text-v7-rust" : "text-v7-success"}${glossClassName}`}>
              {formatSignedPercentage(holding.weight)}
            </span>
          </div>
        );
      })}

      {isPartial && hiddenCount > 0 && (
        <div className="flex flex-col items-center gap-3 border-t border-v7-ink/10 bg-v7-paper px-4 py-6 text-center sm:flex-row sm:justify-between sm:text-left">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-v7-control border border-v7-bronze/30 bg-v7-bronze/10 text-v7-bronze-dark">
              <LockKeyhole className="h-3.5 w-3.5" />
            </span>
            <div>
              <p className="text-sm font-medium text-v7-ink">
                {formatNumber(hiddenCount, 0)} more {hiddenCount === 1 ? "position is" : "positions are"} for {providerName} subscribers
              </p>
              <p className="mt-0.5 text-[13px] leading-[19px] text-v7-ink/60">
                Subscribe on Plutus to see the remaining positions.
              </p>
            </div>
          </div>
          <Link
            to={productDetailPath(productType, providerId, productId)}
            onClick={onSubscribeClick}
            className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-v7-control border border-v7-ink bg-v7-ink px-4 text-[13px] font-medium tracking-[0.04em] text-v7-white transition-colors hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
          >
            View on Plutus
          </Link>
        </div>
      )}
    </div>
  );
};

export default ProductShareUnlockedHoldings;
